import {
  Injectable,
  NgZone,
  ComponentRef,
  ApplicationRef,
  ComponentFactoryResolver,
  Injector,
  inject,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { CmUiDialogService, TokenService } from '@cm/ui-modules';
import { fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, takeUntil, filter } from 'rxjs/operators';
import { InactivityModalComponent } from '../components/inactivity-modal/inactivity-modal.component';
import { CountdownComponent } from '../components/countdown/countdown.component';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root',
})
export class InactivityMonitorService {
  private zone = inject(NgZone);
  private router = inject(Router);
  private tokenService = inject(TokenService);
  private dialog = inject(CmUiDialogService);
  private appRef = inject(ApplicationRef);
  private componentFactoryResolver = inject(ComponentFactoryResolver);
  private injector = inject(Injector);
  private configService = inject(ConfigService);

  private destroy$ = new Subject<void>();
  private timer: any;
  private countdownRef!: ComponentRef<CountdownComponent>;
  private expireSession: boolean = true;
  private expire: boolean = false;

  constructor() {
    this.observeConfig();
  }

  private observeConfig(): void {
    this.configService.getAppConfig().subscribe((config) => {
      if (config && config.configuration && config.configuration.expireSession) {
        const expireSessionValue = +config.configuration.expireSession;
        this.expire = expireSessionValue !== 0 ? true : false;
        this.expireSession = expireSessionValue !== 0 ? true : false;
      }
    });
  }

  startMonitoring(): void {
    this.zone.runOutsideAngular(() => {
      const activity$ = merge(fromEvent(document, 'mousemove'), fromEvent(document, 'click'));

      activity$
        .pipe(debounceTime(100), takeUntil(this.destroy$))
        .subscribe(() => this.checkRouteAndResetTimer());

      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          takeUntil(this.destroy$),
        )
        .subscribe(() => this.checkRouteAndResetTimer());

      this.checkRouteAndResetTimer();
    });
  }

  private checkRouteAndResetTimer(): void {
    if (this.isAuthRoute() || !this.expireSession) {
      this.clearTimer();
      this.removeCountdown();
    } else {
      this.resetTimer();
    }
  }

  private isAuthRoute(): boolean {
    return this.router.url.startsWith('/authenticate/');
  }

  private resetTimer(): void {
    this.clearTimer();
    if (this.expire) {
      //const expirationTime = this.getTokenExpirationTime();
      const expirationTime = new Date(Date.now() + 15 * 60 * 1000);
      if (expirationTime) {
        const timeUntilExpiration = expirationTime.getTime() - Date.now();
        this.timer = setTimeout(() => this.handleInactivity(), timeUntilExpiration);
        this.showCountdown(expirationTime);
      } else {
        // If we can't determine the expiration time, use a default (e.g., 1 hour)
        const defaultExpiration = new Date(Date.now() + 15 * 60 * 1000);
        this.timer = setTimeout(() => this.handleInactivity(), 15 * 60 * 1000);
        this.showCountdown(defaultExpiration);
      }
    }
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private getTokenExpirationTime(): Date | null {
    const token = this.tokenService.getAccessToken();
    if (!token) {
      return null;
    }

    const decoded = this.tokenService.decodeToken(token) as any;
    return decoded?.exp ? new Date(decoded.exp * 1000) : null;
  }

  private handleInactivity(): void {
    this.tokenService.clearSession();
    this.removeCountdown();
    this.showInactivityModal();
  }

  private showInactivityModal(): void {
    this.zone.run(() => {
      const dialogRef = this.dialog.open<InactivityModalComponent, unknown, string>(
        InactivityModalComponent,
        {
          width: '400px',
          disableClose: true,
          panelClass: ['inactivity-modal-overlay', 'mat-dialog-transparent'],
          backdropClass: 'inactivity-modal-overlay',

          data: {
            title: 'Inactivity Detected',
            message:
              'You were inactive. For security reasons we have logged you out. Please log in again to continue',
            buttonText: 'Go to Login',
            expirationTime: this.getTokenExpirationTime(),
          },
        },
      );

      dialogRef.closed.subscribe((result) => {
        if (result === 'login') {
          this.router.navigate(['/app/authenticate/login']);
        }
      });
    });
  }

  private showCountdown(expirationTime: Date): void {
    this.zone.run(() => {
      if (this.countdownRef) {
        this.removeCountdown();
      }

      const factory = this.componentFactoryResolver.resolveComponentFactory(CountdownComponent);
      this.countdownRef = factory.create(this.injector);
      this.countdownRef.instance.expirationTime = expirationTime;

      document.body.appendChild(this.countdownRef.location.nativeElement);
      this.appRef.attachView(this.countdownRef.hostView);
    });
  }

  private removeCountdown(): void {
    if (this.countdownRef) {
      this.appRef.detachView(this.countdownRef.hostView);
      this.countdownRef.destroy();
    }
  }

  stopMonitoring(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearTimer();
    this.removeCountdown();
  }
}
